import { ChevronDown, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";

import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";
import { IgGrayPrimitives } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { shimmerTextSx } from "@intelligems/ig-theme/utils/shimmerTextSx";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";

import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";

import { WithDebugMessages } from "./WithDebugMessages";
import {
  buildWorkLogSteps,
  currentWorkLogStep,
  WORK_LOG_DONE_LABEL,
  WORK_LOG_ICON,
  workLogHeaderLabel,
} from "./work-log";
import { StepTimeline } from "@src/components/steps/StepTimeline";

/**
 * The header shimmer's light edge, Figma's #9e9e9e. The palette has no stop
 * there, so it is mixed from the two grays either side of it: #8a8a8a and
 * #b5b5b5 at 46.5% land on 158 (0x9e) per channel.
 */
const HEADER_SHIMMER_EDGE = `color-mix(in srgb, ${IgGrayPrimitives.A100} 46.5%, ${IgGrayPrimitives.A200})`;

type ThoughtProcessGroupProps = {
  parts: FlattenedPart[];
  isLastTurn?: boolean;
  /**
   * Whether this log is the last thing in its turn. A turn can log work, say
   * something, then log more: only the trailing log of the streaming turn is
   * still being written.
   */
  isTrailing?: boolean;
} & MessageFlags;

/**
 * Figma "Atlas / Work-log" (151:285): the turn's reasoning and tool steps as
 * one collapsible task list.
 *
 * While Atlas works the list is open under a shimmering header, the step still
 * in progress shimmering at the bottom; collapsed, the header names that step
 * instead (see `workLogHeaderLabel`). When the task completes the list folds to
 * a flat "Task completed" header and ends in a "Done" row; a click opens it
 * again. A step already on the list is never listed twice
 * (`buildWorkLogSteps`).
 *
 * The header button is only as wide as its label and chevron, so clicking the
 * empty column beside it does nothing. The rows are a `StepTimeline`.
 */
export const ThoughtProcessGroup = ({
  parts,
  isLastTurn,
  isTrailing = true,
  ...flags
}: ThoughtProcessGroupProps) => {
  const inProgress = !!flags.isStreaming && !!isLastTurn && isTrailing;

  // Open while working, folded once complete — re-seeded whenever the log
  // crosses from one to the other, so completing collapses it even if the
  // merchant had toggled it mid-run. Within a phase the merchant's toggle
  // stands.
  const [disclosure, setDisclosure] = useImmer({
    phase: inProgress,
    open: inProgress,
  });
  if (disclosure.phase !== inProgress) {
    setDisclosure(() => ({ phase: inProgress, open: inProgress }));
  }
  const open = disclosure.phase === inProgress ? disclosure.open : inProgress;

  const steps = buildWorkLogSteps(parts, flags.conversationTools);
  const seenSteps = useRef<Set<string> | null>(null);
  if (seenSteps.current === null) {
    seenSteps.current = new Set(steps.map((step) => step.key));
  }
  const newSteps = new Set(
    steps
      .filter((step) => !seenSteps.current?.has(step.key))
      .map((step) => step.key)
  );
  useEffect(() => {
    steps.forEach((step) => seenSteps.current?.add(step.key));
  }, [steps]);
  const reducedMotion = useReducedMotion();
  const current = currentWorkLogStep(parts, steps, flags.conversationTools);
  const label = workLogHeaderLabel({ inProgress, open, current });
  // Nothing to expand until a step lands — whatever the status, a lone "Done"
  // is not content — so the header carries no chevron and no toggle until
  // then (Figma Work-log `State=empty`, ENG-7894).
  const hasContent = steps.length > 0;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <Stack
      data-testid="work-log"
      gap={2}
      alignItems="flex-start"
      sx={{ py: 1.5, width: "100%" }}
    >
      <ButtonBase
        aria-expanded={hasContent ? open : undefined}
        disabled={!hasContent}
        onClick={() =>
          setDisclosure((draft) => {
            draft.open = !open;
          })
        }
        sx={{ borderRadius: "6px", display: "inline-flex", gap: 2, py: 1 }}
      >
        <IgTypography
          variant="body2"
          sx={[
            {
              color: IgCoreTextColors.secondary,
              fontWeight: 600,
              lineHeight: "18px",
              whiteSpace: "nowrap",
            },
            // Figma's header stops are #9e9e9e → #4a4a4a → #9e9e9e.
            inProgress &&
              shimmerTextSx(HEADER_SHIMMER_EDGE, IgCoreTextColors.secondary),
          ]}
        >
          {label}
        </IgTypography>
        {hasContent && (
          <Chevron size={16} color={IgCoreTextColors.secondary} aria-hidden />
        )}
      </ButtonBase>
      {open && hasContent && (
        <StepTimeline
          steps={[
            ...steps.map((step) => ({
              id: step.key,
              label: step.label,
              status:
                inProgress && step.key === current?.key
                  ? ("running" as const)
                  : ("done" as const),
              icon: WORK_LOG_ICON[step.kind],
            })),
            // A completed log closes with a "Done" row after its steps.
            ...(inProgress
              ? []
              : [
                  {
                    id: "done",
                    label: WORK_LOG_DONE_LABEL,
                    status: "done" as const,
                    icon: WORK_LOG_ICON.done,
                  },
                ]),
          ]}
          renderStep={(timelineStep, row) => {
            const step = steps.find((s) => s.key === timelineStep.id);
            return step ? (
              <WithDebugMessages
                item={step.part}
                part={step.part.part}
                debug={flags.debug}
              >
                {/* A step that arrives after the log first drew fades in. */}
                <motion.div
                  data-testid="work-log-row"
                  initial={
                    newSteps.has(step.key) && !reducedMotion
                      ? { opacity: 0 }
                      : false
                  }
                  animate={{ opacity: 1 }}
                  transition={
                    reducedMotion
                      ? { duration: 0 }
                      : { duration: 0.24, ease: [0, 0, 0.2, 1] }
                  }
                >
                  {row}
                </motion.div>
              </WithDebugMessages>
            ) : (
              row
            );
          }}
        />
      )}
    </Stack>
  );
};
